import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
//import { Box } from '@mui/material';
import { useFormContext } from './FormContext';
import { getSubmitButtonConfig, getDefaultSubmitButtonConfig } from '../utils/firestore';
import { safeGetItem, safeSetItem } from '../utils/storageUtils';
import { decompressData } from '../utils/decompressionUtils'; 
import { getDoc, doc } from 'firebase/firestore'; 
import { db } from '../firebase'; 

// version-aware blob selection (draftOverlay vs root) aligned with other contexts
import { resolveSubmissionSettings, getFormVersionSnapshot } from '../utils/formVersionResolver';

// Import submission UI components
import DisplayCardPreview from "../components/FormSettings/FormSubmitSettings/DisplayCardPreview";
import DisplayTablePreview from "../components/FormSettings/FormSubmitSettings/DisplayTablePreview";   // 🆕

const TABLE_SURFACE_SETTINGS_KEYS = new Set([
  'addToSubmissionsTable',
  'submissionsPageTable',
]);

const TABLE_SURFACE_ONLY_SETTINGS_KEYS = new Set([
  'submissionsPageTable',
]);

const isTableSurfaceSettingsKey = (key) =>
  TABLE_SURFACE_SETTINGS_KEYS.has(key);

const isTableSurfaceOnlySettingsKey = (key) =>
  TABLE_SURFACE_ONLY_SETTINGS_KEYS.has(key);

const isPlainSettingsObject = (value) =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value);

const FormSubmissionSettingsContext = createContext();
export const useFormSubmissionSettings = () => useContext(FormSubmissionSettingsContext);

export const FormSubmissionSettingsProvider = ({ children }) => {
  const { formId } = useFormContext();

  // ────────────────────────────────────────────────────────────────
  // Route-aware flags (builder vs viewer) – mirrors other contexts
  // ────────────────────────────────────────────────────────────────
  const isPublicPreviewMode = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('isPublicPreview');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value === true;
    } catch (e) {
      return false;
    }
  }, []);

  const isBuilderRoute = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.startsWith('/form-builder');
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Mode-aware submissionSettings cache keys (aligned with FormLayoutContext)
  // - Builder route: honor builder view mode (auto/published/draftOverlay)
  // - Viewer route (/forms/:id): ALWAYS force published
  // ────────────────────────────────────────────────────────────────
  const builderViewMode = useMemo(() => {
    try {
      if (!formId) return 'auto';
      const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value || 'auto'; // 'auto' | 'published' | 'draftOverlay'
    } catch (e) {
      return 'auto';
    }
  }, [formId]);

  const effectiveViewMode = useMemo(() => {
    if (isBuilderRoute) return builderViewMode;
    return 'published';
  }, [isBuilderRoute, builderViewMode]);

  // Mode-aware cache keys (mirror legacy keys during refactor)
  const submissionSettingsKey = useMemo(() => {
    if (!formId) return null;
    return `submissionSettings_${formId}__${effectiveViewMode}`;
  }, [formId, effectiveViewMode]);

  const lastUpdatedKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_submissionLastUpdated__${effectiveViewMode}`;
  }, [formId, effectiveViewMode]);

  // Legacy keys (still used by not-yet-refactored paths)
  const legacySubmissionSettingsKey = useMemo(() => {
    if (!formId) return null;
    return `submissionSettings_${formId}`;
  }, [formId]);

  const legacyLastUpdatedKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_submissionLastUpdated`;
  }, [formId]);

  // 1) Memoize defaultSubmissionSettings so it's stable across renders
const defaultSubmissionSettings = useMemo(() => {
  // console.log('[FormSubmissionSettingsContext] Building defaultSubmissionSettings via useMemo.');
  const defaultConfig = getDefaultSubmitButtonConfig();

    // Build an object from the defaultConfig actions
    // Here we add a default errorMessage and, for any action that supports a custom message,
    // we add a copyIconEnabled property (set to false by default). This makes the copy functionality modular.
    const actionMap = defaultConfig.actions.reduce((acc, action) => {
      // Create an updated action with enabled = false
      const updatedAction = { ...action, enabled: false };

    // seed an empty columnWidths map for the table action
    if (updatedAction.type === 'addToSubmissionsTable') {
      updatedAction.columnOrder  = [];
      updatedAction.columnWidths = {}; 
    }

      // If errorMessage is not provided or is an empty string, set a default error message.
      if (!updatedAction.errorMessage || updatedAction.errorMessage.trim() === '') {
        updatedAction.errorMessage = 'Not a valid submission. Please fill out all required fields.';
      }

      // If the action supports a customMessage (e.g., displayInCard, displayInTable, etc.),
      // add the copyIconEnabled property (defaulting to false).
      if (updatedAction.customMessage !== undefined) {
        updatedAction.copyIconEnabled = false;
      }

      acc[action.type] = updatedAction;
      return acc;
    }, {});



  // Ensure displayInCard has a string-based customMessage for HTML
  if (actionMap.displayInCard && typeof actionMap.displayInCard.customMessage !== 'string') {
    // console.log('[FormSubmissionSettingsContext] Forcing default displayInCard.customMessage to an empty string (HTML).');
    actionMap.displayInCard.customMessage = '';
  }
//    console.log('[FormSubmissionSettingsContext] Default submissionSettings:', actionMap);

  return actionMap;
}, []);

  /**
   * normalizeTableAction – makes sure addToSubmissionsTable is well-formed
   * and keeps unknown/extra keys (e.g., virtual column metadata: kind/role/ufi).
   */
  const normalizeTableAction = useCallback((action) => {
    const nxt = { ...(action || {}) };
    if (!Array.isArray(nxt.columnOrder)) nxt.columnOrder = [];
    if (!nxt.columnWidths || typeof nxt.columnWidths !== 'object') nxt.columnWidths = {};

  // optional hardening: always provide a boolean for the UI
  if (typeof nxt.enableStickyHeader !== 'boolean') nxt.enableStickyHeader = false;

  // ── normalize columnOrder items but preserve unknown keys ─────
    nxt.columnOrder = nxt.columnOrder
      .filter(Boolean)
      .map((c, i) => (typeof c === 'object' ? c : { id: String(c), visible: true, order: i }))
      .map((c, i) => ({
        // Preserve **all** existing keys to keep virtual columns metadata (kind/role/ufi) intact
        ...c,
        id: String(c.id || ''),
        visible: c.visible !== false,
        order: Number.isFinite(c.order) ? c.order : i,
      }));
    return nxt;
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Submission action identity
  // ----------------------------------------------------------------
  // Enterprise contract:
  // - defaultSubmissionSettings is the canonical submit-action schema.
  // - Table surfaces may live in submissionSettings, but only keys from
  //   defaultSubmissionSettings are allowed to participate in action
  //   selection / enable-disable behavior.
  // - This prevents submissionsPageTable from being treated as a submit
  //   action and prevents undefined branches from crashing .enabled reads.
  // ────────────────────────────────────────────────────────────────
  const submissionActionKeys = useMemo(() => {
    return Object.keys(defaultSubmissionSettings || {});
  }, [defaultSubmissionSettings]);

  const submissionActionTypes = useMemo(() => {
    return new Set(submissionActionKeys);
  }, [submissionActionKeys]);

  /**
   * normalizeSubmissionAction
   * ----------------------------------------------------------------
   * Normalizes one submit action branch by merging it into the canonical
   * default action shape.
   *
   * Enterprise rules:
   * - Every submit action branch must be an object.
   * - Every submit action branch must have a boolean enabled flag.
   * - addToSubmissionsTable keeps table surface structure because it is both
   *   an action and the in-form Display Table surface.
   * - Unknown/extra action properties are preserved for integrations,
   *   runtime display hints, and future providers.
   */
  const normalizeSubmissionAction = useCallback((actionType, rawAction) => {
    const defaultAction =
      defaultSubmissionSettings?.[actionType] &&
      typeof defaultSubmissionSettings[actionType] === 'object'
        ? defaultSubmissionSettings[actionType]
        : {};

    const rawActionObject = isPlainSettingsObject(rawAction)
      ? rawAction
      : {};

    let normalizedAction = {
      ...defaultAction,
      ...rawActionObject,
      enabled: rawActionObject.enabled === true,
    };

    // Preserve the existing displayInCard hardening behavior.
    // Keep this generic enough for any action that supports customMessage.
    if (
      normalizedAction.customMessage !== undefined &&
      typeof normalizedAction.customMessage !== 'string'
    ) {
      console.warn(
        '[FormSubmissionSettingsContext] Fixing action customMessage type.',
        {
          actionType,
          receivedType: typeof normalizedAction.customMessage,
        }
      );
      normalizedAction.customMessage =
        String(normalizedAction.customMessage);
    }

    if (
      !normalizedAction.errorMessage ||
      typeof normalizedAction.errorMessage !== 'string' ||
      normalizedAction.errorMessage.trim() === ''
    ) {
      normalizedAction.errorMessage =
        defaultAction.errorMessage ||
        'Not a valid submission. Please fill out all required fields.';
    }

    if (normalizedAction.copyIconEnabled !== undefined) {
      normalizedAction.copyIconEnabled =
        normalizedAction.copyIconEnabled === true;
    }

    // addToSubmissionsTable is both:
    // - a submit action
    // - the in-form Display Table surface
    //
    // Therefore it must always carry the table surface shape.
    if (actionType === 'addToSubmissionsTable') {
      normalizedAction = normalizeTableAction(normalizedAction);
    }

    return normalizedAction;
  }, [
    defaultSubmissionSettings,
    normalizeTableAction,
  ]);

  /**
   * normalizeSubmissionSettings
   * ----------------------------------------------------------------
   * Canonicalizes the entire submissionSettings object before it reaches
   * UI code, storage, save/publish, or action execution.
   *
   * Enterprise rules:
   * - Merge all submit actions into defaultSubmissionSettings.
   * - Drop undefined/null branches rather than allowing Object.keys(...)
   *   to expose keys whose values crash on `.enabled`.
   * - Preserve unknown top-level objects for forward compatibility.
   * - Treat submissionsPageTable as a table surface only.
   * - Preserve provider/virtual column metadata such as kind/role/ufi,
   *   provider, outputKey, metricKey, sourceUfi, copyEnabled, etc.
   */
  const normalizeSubmissionSettings = useCallback((rawSettings = {}) => {
    const raw = isPlainSettingsObject(rawSettings) ? rawSettings : {};

    const normalized = submissionActionKeys.reduce((acc, actionType) => {
      acc[actionType] = normalizeSubmissionAction(
        actionType,
        raw[actionType]
      );
      return acc;
    }, {});

    // Preserve top-level non-action settings without letting undefined/null
    // branches become state keys.
    Object.entries(raw).forEach(([key, value]) => {
      if (submissionActionTypes.has(key)) {
        return;
      }

      if (value === undefined || value === null) {
        return;
      }

      if (key === 'submissionsPageTable') {
        normalized.submissionsPageTable =
          normalizeTableAction(value);
        return;
      }

      normalized[key] = isPlainSettingsObject(value)
        ? { ...value }
        : value;
    });

    /* 🔧 prune legacy 'thumb@' virtual rows (base attach/image now renders thumb) */
    normalized.addToSubmissionsTable.columnOrder =
      (normalized.addToSubmissionsTable.columnOrder || []).filter(
        (c) => !String(c?.id || '').startsWith('thumb@')
      );

    if (normalized.submissionsPageTable?.columnOrder) {
      normalized.submissionsPageTable.columnOrder =
        (normalized.submissionsPageTable.columnOrder || []).filter(
          (c) => !String(c?.id || '').startsWith('thumb@')
        );
    }

    return normalized;
  }, [
    submissionActionKeys,
    submissionActionTypes,
    normalizeSubmissionAction,
    normalizeTableAction,
  ]);


  // 2) Initialize state with the memoized defaults
  const [submissionSettings, setSubmissionSettings] = useState(defaultSubmissionSettings);
  const [isLoading, setIsLoading] = useState(false); // Added missing state
  const [error, setError] = useState(null); // Added missing error state

  const activeActionType = useMemo(() => {
    return submissionActionKeys.find(
      (key) => submissionSettings?.[key]?.enabled === true
    ) || null;                                    // → "displayInCard", "redirect", etc.
  }, [submissionSettings, submissionActionKeys]);

  const isDisplayInCardEnabled =
    submissionSettings.displayInCard?.enabled === true;

/**
 * 3) useEffect to load initial settings whenever formId changes.
 *    - If no formId, just use defaultSubmissionSettings.
 *    - Otherwise, check session/local storage first (safeGetItem).
 *    - If nothing found, attempt to fetch from Firestore, then fallback to default.
 */
useEffect(() => {
  if (!formId) {
    // ENTERPRISE:
    // A missing formId can be a normal transient state while FormContext resolves
    // route params, or a legitimate new/unsaved builder state. Do not warn here.
    //
    // Keep default settings available, but avoid unnecessary state churn by
    // returning the previous value when it is already the default object.
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[FormSubmissionSettingsContext] formId not available yet; using default submission settings.');
    }

    setSubmissionSettings((prev) =>
      prev === defaultSubmissionSettings ? prev : defaultSubmissionSettings
    );

    setIsLoading((prev) => (prev === false ? prev : false));
    setError((prev) => (prev === null ? prev : null));
    return;
  }

  const loadSettings = async () => {
    setIsLoading(true);
    setError(null);

    // Mode-aware active keys + legacy fallback (NO naming collisions)
    const activeSettingsKey =
      submissionSettingsKey || `submissionSettings_${formId}__published`;
    const activeLastUpdatedKey =
      lastUpdatedKey || `form_${formId}_submissionLastUpdated__published`;

    const legacyKey =
      legacySubmissionSettingsKey || `submissionSettings_${formId}`;
    const legacyUpdatedKey =
      legacyLastUpdatedKey || `form_${formId}_submissionLastUpdated`;

/* ────────────────────────────────────────────────
   STEP 1 - Try session/local storage first
───────────────────────────────────────────────── */

// ✅ Step 1a: Viewer route should NOT read/apply cache first.
// We always want Firestore to be the source of truth for published viewing,
// otherwise stale localStorage can override newly published settings.
// Builder + PublicPreview can still use cache-first for speed.
const allowCacheFirst = (isBuilderRoute || isPublicPreviewMode);

// Mode-aware first, legacy fallback (aligned with FormLayoutContext)
// NOTE: only read these when allowCacheFirst is true.
const storedActiveRaw = allowCacheFirst ? safeGetItem(activeSettingsKey) : null;
const storedLegacyRaw = allowCacheFirst ? safeGetItem(legacyKey) : null;

const storedDataRaw = storedActiveRaw || storedLegacyRaw;

// Also only read lastUpdated keys when allowCacheFirst is true
const localLastUpdated = allowCacheFirst
  ? (
      safeGetItem(activeLastUpdatedKey) ||
      safeGetItem(legacyUpdatedKey)
    )
  : null;

if (storedDataRaw) {
  // ✅ avoid mutating the cached object returned by safeGetItem
  // Enterprise: always normalize the full settings object before it reaches
  // state. This prevents partial duplicated/local forms from carrying
  // undefined action branches such as displayInCard: undefined.
  const storedData = normalizeSubmissionSettings(storedDataRaw);

  setSubmissionSettings(storedData);

  // ────────────────────────────────────────────────────────────
  // - Viewer is always 'published' mode → safe to short-circuit on either cache
  // - Builder in 'auto' or 'draftOverlay':
  //     - short-circuit ONLY if we have the mode-aware key
  //     - if we only have legacy, continue to Firestore so overlay can load
  // ────────────────────────────────────────────────────────────
  const canShortCircuit =
    // Viewer route must NOT short-circuit on cache.
    // We always want Firestore to be the source of truth for published viewing.
    (!isBuilderRoute && !isPublicPreviewMode)
      ? false
      : (
          // Builder/public-preview:
          // - short-circuit ONLY if we have the mode-aware key
          // - if we only have legacy, continue to Firestore so overlay can load
          !!storedActiveRaw
        );

  if (canShortCircuit) {
    setIsLoading(false);
    return;
  }

  // Otherwise: continue to Firestore (do NOT stop at legacy in non-published builder modes)
}


    console.warn(`[FormSubmissionSettingsContext] No stored data found for formId: ${formId}, checking Firestore…`);

    /* ────────────────────────────────────────────────
       STEP 2 - Pull compressed submissionSettings blob
                 from Firestore (if any)
    ───────────────────────────────────────────────── */
    try {
      const formRef = doc(db, 'forms', formId);
      const formDoc = await getDoc(formRef);

      if (!formDoc.exists()) {
        console.warn(`[FormSubmissionSettingsContext] No Firestore data found for formId: ${formId}`);
        setIsLoading(false);
        return;
      }

      const formData = formDoc.data() || {};

      // NEW: version-aware resolve for submissionSettings (draftOverlay vs root)
      const versionSnap = getFormVersionSnapshot(formData);
      const resolvedSubm = resolveSubmissionSettings(formData, { viewMode: effectiveViewMode, debug: true });

      console.log('[FSSC:loadSettings] version snapshot:', versionSnap);
      console.log('[FSSC:loadSettings] resolved submissionSettings source:', resolvedSubm);

      const compressedData = resolvedSubm?.value;

      // Source-aware lastUpdated:
      // - overlay: draftOverlaySavedAt / draftOverlay.savedAt
      // - root: lastModified
      const firestoreLastUpdated = (() => {
        try {
          if (resolvedSubm?.source === 'draftOverlay') {
            const t =
              formData?.draftOverlaySavedAt ||
              formData?.draftOverlay?.savedAt ||
              null;
            if (t && typeof t.toDate === 'function') return t.toDate().toISOString();
            if (t instanceof Date) return t.toISOString();
          }
          const lm = formData?.lastModified;
          if (lm && typeof lm.toDate === 'function') return lm.toDate().toISOString();
          if (lm instanceof Date) return lm.toISOString();
        } catch (e) {}
        return null;
      })();

      /* STEP 3 – skip Firestore if our local copy is newer
         BUT ONLY in builder/public-preview. Viewer should always trust Firestore. */
      if (
        (isBuilderRoute || isPublicPreviewMode) &&
        localLastUpdated &&
        firestoreLastUpdated &&
        localLastUpdated > firestoreLastUpdated
      ) {
        console.warn('[FormSubmissionSettingsContext] Local data is newer than Firestore. Skipping Firestore overwrite.');
        setIsLoading(false);
        return;
      }

      /* STEP 4 - If Firestore has a compressed blob, decompress it */
      if (compressedData) {
      //  console.log('[FormSubmissionSettingsContext] Decompressing submission settings from Firestore…');
        const decompressedSettingsRaw = await decompressData(compressedData);
      //  console.log('[FormSubmissionSettingsContext] Firestore submission settings decompressed:', decompressedSettingsRaw);

        /* 🔹 Guarantee / normalize the table keys (centralized) */
        //    - Ensures addToSubmissionsTable exists (object)
        //    - Ensures columnOrder (array), columnWidths (object), enableStickyHeader (boolean)
        //    - Preserves any extra keys on column items (e.g., kind/role/ufi for virtual columns)
        //    - Ensures all submit actions have stable object branches with boolean enabled flags
        //    - Keeps submissionsPageTable as a table surface, not a submit action
        const decompressedSettings =
          normalizeSubmissionSettings(decompressedSettingsRaw);

        /* STEP 5 – cache & set state */
        // Cache mode-aware (authoritative for this view mode)
        safeSetItem(activeSettingsKey, decompressedSettings);
        safeSetItem(activeLastUpdatedKey, firestoreLastUpdated || new Date().toISOString());

        // Align with “viewer never shows overlay” guarantee:
        // Only mirror to legacy when we are in published view AND the resolver chose root.
        if (effectiveViewMode === 'published' && resolvedSubm?.source === 'root') {
          safeSetItem(legacyKey, decompressedSettings);
          safeSetItem(legacyUpdatedKey, firestoreLastUpdated || new Date().toISOString());
        }

        setSubmissionSettings(decompressedSettings);
        setIsLoading(false);
        return;
      }

      /* STEP 6 - no compressed blob: fall back to legacy Firestore config */
      console.warn('[FormSubmissionSettingsContext] No compressed submissionSettings blob; falling back to getSubmitButtonConfig…');
      const { status, config } = await getSubmitButtonConfig(formId);

      if (status === 'success' && config?.actions) {
        const firestoreSettingsRaw = config.actions.reduce((acc, action) => {
          acc[action.type] = { ...action, enabled: false };
          return acc;
        }, {});

        /* 🔹 seed table keys in the fallback object as well */
        const firestoreSettings =
          normalizeSubmissionSettings(firestoreSettingsRaw);

        /* STEP 7 – cache & set state */
        safeSetItem(activeSettingsKey, firestoreSettings);
        safeSetItem(activeLastUpdatedKey, firestoreLastUpdated || new Date().toISOString());

        safeSetItem(legacyKey, firestoreSettings);
        safeSetItem(legacyUpdatedKey, firestoreLastUpdated || new Date().toISOString());

        setSubmissionSettings(firestoreSettings);
        setIsLoading(false);
        return;
      }

      /* STEP 8 – final fallback to defaults */
      console.warn('[FormSubmissionSettingsContext] No valid submission settings found in Firestore. Falling back to defaults.');
      setSubmissionSettings(defaultSubmissionSettings);
    } catch (err) {
      console.error('[FormSubmissionSettingsContext] Error fetching submission settings from Firestore:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  loadSettings();
}, [
  formId,
  defaultSubmissionSettings,
  normalizeTableAction,
  normalizeSubmissionSettings,
  isBuilderRoute,
  isPublicPreviewMode,
  effectiveViewMode,
  submissionSettingsKey,
  lastUpdatedKey,
  legacySubmissionSettingsKey,
  legacyLastUpdatedKey,
]);



/**
 * 4) A function to save updated settings:
 *    - Store them with safeSetItem (which puts them in both session/local).
 *    - We comment out any further merges/updates to Firestore for now.
 */
const saveSettings = useCallback(
  (updatedSettings) => {
    if (!formId) {
      console.warn('[FormSubmissionSettingsContext] No formId, cannot save settings.');
      return;
    }

    // console.log(`[FormSubmissionSettingsContext] Saving updated settings for formId: ${formId}`, updatedSettings);

    // Enterprise: never persist or expose a partial settings shape.
    // This centralizes all schema repair for duplicated forms, old local cache,
    // draft overlays, published blobs, provider columns, and table surfaces.
    const normalizedSettings = normalizeSubmissionSettings(updatedSettings);

    /* 🔹 Always coerce displayInCard.customMessage to string */
    if (normalizedSettings.displayInCard?.customMessage === undefined) {
      normalizedSettings.displayInCard.customMessage = '';
    }

    /* 🔹 Make sure the table action still has both structural keys before persisting */
    if (!normalizedSettings.addToSubmissionsTable) normalizedSettings.addToSubmissionsTable = {};
    normalizedSettings.addToSubmissionsTable =
      normalizeTableAction(normalizedSettings.addToSubmissionsTable);

    // Enterprise table-surface split:
    // - addToSubmissionsTable remains the in-form Display Table action.
    // - submissionsPageTable is the dedicated Submissions Page view preference.
    // - Normalize it when present, but do not create it unless the page actually
    //   saves a page-specific column model.
    if (normalizedSettings.submissionsPageTable) {
      normalizedSettings.submissionsPageTable =
        normalizeTableAction(normalizedSettings.submissionsPageTable);
    }

    /* 🔹 Create a shallow copy for storage so we can strip big runtime-only blobs (submittedValues) */
    const copyForLocalStorage = { ...normalizedSettings };

    if (copyForLocalStorage.displayInCard) {
      copyForLocalStorage.displayInCard = {
        ...copyForLocalStorage.displayInCard,
      };
      if ('submittedValues' in copyForLocalStorage.displayInCard) {
        delete copyForLocalStorage.displayInCard.submittedValues;
        // console.log('[FormSubmissionSettingsContext] stripped out submittedValues before saving to local storage.');
      }
    }

    /* NOTE – we DO persist columnOrder / columnWidths exactly as they are */
    //    console.log(
    //      '[FSSC] columnOrder after save:',
    //      copyForLocalStorage.addToSubmissionsTable.columnOrder.map(c => ({
    //        id: c.id,
    //        isCalc: !!c.isCalculated,
    //        visible: c.visible
    //      }))
    //    );

    const activeKey =
      submissionSettingsKey || `submissionSettings_${formId}__published`;
    const activeUpdatedKey =
      lastUpdatedKey || `form_${formId}_submissionLastUpdated__published`;

    const legacyKey =
      legacySubmissionSettingsKey || `submissionSettings_${formId}`;
    const legacyUpdatedKey =
      legacyLastUpdatedKey || `form_${formId}_submissionLastUpdated`;

    safeSetItem(activeKey, copyForLocalStorage);
    safeSetItem(activeUpdatedKey, new Date().toISOString());

    // Mirror legacy only when editing published view.
    // Prevents draft overlay edits from leaking into viewer via legacy fallback.
    if (effectiveViewMode === 'published') {
      safeSetItem(legacyKey, copyForLocalStorage);
      safeSetItem(legacyUpdatedKey, new Date().toISOString());
    }


    /* Update in-memory state with the full object (including submittedValues) */
    setSubmissionSettings(normalizedSettings);
  },
  [
    formId,
    effectiveViewMode,
    submissionSettingsKey,
    lastUpdatedKey,
    legacySubmissionSettingsKey,
    legacyLastUpdatedKey,
    normalizeSubmissionSettings,
    normalizeTableAction,
  ]
);

  /**
   * 5) A function to update the settings for a specific submission action.
   *    - Merges partial changes into that action.
   *    - Sets enabled = true by default for the updated action and disables all other actions.
   *    - Skips saving if nothing actually changed (to avoid redundant calls).
   *
   * Note: For any submission action that supports an error message,
   *       the newSettings object can include an errorMessage.
   *       The updateSettings function will merge errorMessage generically for any action,
   *       so that components like DisplayInCard or DisplayInTable can configure their error messages without extra logic.
   */
  const updateSettings = useCallback(
    (actionType, newSettings = {}) => {
//      console.log('[FSSC] updateSettings →', actionType, newSettings);

      setSubmissionSettings((prevSettings) => {
        if (!formId) {
          console.warn('[FormSubmissionSettingsContext] No formId, cannot update settings.');
          return prevSettings;
        }

        if (!newSettings || typeof newSettings !== 'object' || Array.isArray(newSettings)) {
          console.warn('[FormSubmissionSettingsContext] Invalid action settings patch:', {
            actionType,
            receivedType: Array.isArray(newSettings) ? 'array' : typeof newSettings,
          });
          return prevSettings;
        }

        const normalizedPrevSettings =
          normalizeSubmissionSettings(prevSettings);

        // Guardrail for legacy callers:
        // submissionsPageTable is a table-surface-only key. It must never be
        // treated as a submit action because updateSettings intentionally
        // enables the selected action and disables other actions.
        if (isTableSurfaceOnlySettingsKey(actionType)) {
          console.warn(
            '[FormSubmissionSettingsContext] updateSettings received a table-surface-only key. Routing through table-surface merge path.',
            {
              actionType,
            }
          );

          const currentSurface =
            normalizeTableAction(normalizedPrevSettings?.[actionType] || {});

          const mergedSurface = normalizeTableAction({
            ...currentSurface,
            ...newSettings,
          });

          const changed =
            JSON.stringify(currentSurface) !== JSON.stringify(mergedSurface);

          if (!changed) {
            console.log('[FormSubmissionSettingsContext] No table surface changes detected; skipping save.', {
              settingsKey: actionType,
            });
            return normalizedPrevSettings;
          }

          const updatedSettings = {
            ...normalizedPrevSettings,
            [actionType]: mergedSurface,
          };

          console.log('[FormSubmissionSettingsContext] Table surface settings changed:', {
            settingsKey: actionType,
            columnCount: Array.isArray(mergedSurface.columnOrder)
              ? mergedSurface.columnOrder.length
              : 0,
            widthCount: mergedSurface.columnWidths &&
              typeof mergedSurface.columnWidths === 'object'
              ? Object.keys(mergedSurface.columnWidths).length
              : 0,
          });

          saveSettings(updatedSettings);
          return updatedSettings;
        }

        if (!submissionActionTypes.has(actionType)) {
          console.warn('[FormSubmissionSettingsContext] Invalid submission action type:', {
            actionType,
          });
          return normalizedPrevSettings;
        }

        const currentAction = normalizedPrevSettings[actionType] || {};
      const mergedAction = {
        ...currentAction,
        ...newSettings,
        enabled: true,

        // Ensure that customMessage remains the original rich text template with placeholders.
        customMessage:
          newSettings.customMessage !== undefined
            ? newSettings.customMessage
            : currentAction.customMessage,

        // Merge errorMessage generically for any action.
        errorMessage:
          newSettings.errorMessage !== undefined
            ? newSettings.errorMessage
            : currentAction.errorMessage,

        // Merge copyIconEnabled generically for any action that supports a custom message.
        copyIconEnabled:
          newSettings.copyIconEnabled !== undefined
            ? newSettings.copyIconEnabled
            : currentAction.copyIconEnabled,

        // Merge submittedValues (if present); preserve old one if it's not updated.
//        submittedValues:
//          newSettings.submittedValues !== undefined
//            ? newSettings.submittedValues
//            : currentAction.submittedValues,
      };


        // Create updatedSettings where the selected action is enabled and all other
        // submit actions are disabled. Non-action table surfaces such as
        // submissionsPageTable are preserved untouched.
        const updatedSettings = {
          ...normalizedPrevSettings,
        };

        submissionActionKeys.forEach((key) => {
          if (key === actionType) {
            updatedSettings[key] = mergedAction;
          } else {
            updatedSettings[key] = {
              ...(normalizedPrevSettings[key] || {}),
              enabled: false,
            };
          }
        });

        // 🔹 Shallow compare old vs. new action to see if anything changed for the selected action
        let changed = false;
        const keysToCheck = new Set([
          ...Object.keys(currentAction),
          ...Object.keys(mergedAction),
        ]);
        for (const key of keysToCheck) {
          if (currentAction[key] !== mergedAction[key]) {
            changed = true;
            break;
          }
        }

        // Also check if any other submit action was previously enabled
        // (which we are now disabling). Table-surface-only settings are not
        // submit actions and must not participate in this check.
        const otherEnabled = submissionActionKeys.some(
          (key) => key !== actionType && normalizedPrevSettings[key]?.enabled === true
        );
        if (!changed && !otherEnabled) {
          // console.log(`[FormSubmissionSettingsContext] No changes detected for actionType: ${actionType}, skipping save.`);
          return normalizedPrevSettings;
        }

        // console.log(`[FormSubmissionSettingsContext] Settings changed, saving now...`, updatedSettings);
        saveSettings(updatedSettings);
        return updatedSettings;
      });
    },
    [
      saveSettings,
      formId,
      normalizeSubmissionSettings,
      normalizeTableAction,
      submissionActionKeys,
      submissionActionTypes,
    ]
  );

  /**
   * Updates table-surface settings without toggling submission action state.
   *
   * Enterprise table-surface contract:
   * - updateSettings(actionType, patch) remains the action selector/updater.
   *   It intentionally enables the selected action and disables other actions.
   * - updateTableSurfaceSettings(settingsKey, patch) is for table view
   *   preferences only.
   * - It preserves the currently selected submission action.
   * - It is safe for both:
   *     • addToSubmissionsTable  → in-form Display Table action settings
   *     • submissionsPageTable   → dedicated Submissions Page view settings
   *
   * This prevents the Submissions Page column manager from accidentally
   * disabling Display Card or changing the active submit action.
   */
  const updateTableSurfaceSettings = useCallback(
    (settingsKey, patch = {}) => {
      if (!formId) {
        console.warn('[FormSubmissionSettingsContext] No formId, cannot update table surface settings.');
        return;
      }

      if (!isTableSurfaceSettingsKey(settingsKey)) {
        console.warn('[FormSubmissionSettingsContext] Invalid table surface settings key:', {
          settingsKey,
        });
        return;
      }

      if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
        console.warn('[FormSubmissionSettingsContext] Invalid table surface patch:', {
          settingsKey,
          receivedType: Array.isArray(patch) ? 'array' : typeof patch,
        });
        return;
      }

      setSubmissionSettings((prevSettings) => {
        const normalizedPrevSettings =
          normalizeSubmissionSettings(prevSettings);

        const currentSurface =
          normalizeTableAction(normalizedPrevSettings?.[settingsKey] || {});

        const mergedSurface = normalizeTableAction({
          ...currentSurface,
          ...patch,
        });

        const changed =
          JSON.stringify(currentSurface) !== JSON.stringify(mergedSurface);

        if (!changed) {
          console.log('[FormSubmissionSettingsContext] No table surface changes detected; skipping save.', {
            settingsKey,
          });
          return normalizedPrevSettings;
        }

        const updatedSettings = {
          ...normalizedPrevSettings,
          [settingsKey]: mergedSurface,
        };

        console.log('[FormSubmissionSettingsContext] Table surface settings changed:', {
          settingsKey,
          columnCount: Array.isArray(mergedSurface.columnOrder)
            ? mergedSurface.columnOrder.length
            : 0,
          widthCount: mergedSurface.columnWidths &&
            typeof mergedSurface.columnWidths === 'object'
            ? Object.keys(mergedSurface.columnWidths).length
            : 0,
        });

        saveSettings(updatedSettings);
        return updatedSettings;
      });
    },
    [
      formId,
      normalizeTableAction,
      normalizeSubmissionSettings,
      saveSettings,
    ]
  );

  // ────────────────────────────────────────────────────────────────
  // Runtime-only patch (enterprise-safe)
  // ----------------------------------------------------------------
  // Use for preview-time payloads that should NOT:
  //   • toggle enabled actions
  //   • persist to local/session storage
  //
  // Example:
  //   patchActionRuntime('displayInCard', { submittedRows: [...] })
  // ────────────────────────────────────────────────────────────────
  const patchActionRuntime = useCallback((actionType, patch) => {
    if (!actionType || !patch || typeof patch !== 'object') return;

    setSubmissionSettings((prev) => {
      const normalizedPrev = normalizeSubmissionSettings(prev);

      if (!submissionActionTypes.has(actionType)) {
        console.warn('[FormSubmissionSettingsContext] Ignoring runtime patch for non-action settings key:', {
          actionType,
        });
        return normalizedPrev;
      }

      const current = normalizedPrev?.[actionType] || {};
      const nextAction = { ...current, ...patch };

      // No-op if nothing changed (prevents re-render storms)
      const keys = new Set([...Object.keys(current), ...Object.keys(patch)]);
      let changed = false;
      for (const k of keys) {
        if (current[k] !== nextAction[k]) { changed = true; break; }
      }
      if (!changed) return normalizedPrev;

      return {
        ...normalizedPrev,
        [actionType]: nextAction,
      };
    });
  }, [
    normalizeSubmissionSettings,
    submissionActionTypes,
  ]);

  /**
   * 6️⃣ Dynamically Determine the UI Component for the Current Submission Setting
   *
   * Enterprise presentation contract:
   * - This context still owns action selection: Display Card vs Display Table, etc.
   * - The consuming page may explicitly control presentation details that are
   *   route/surface-specific, such as whether submitted Display Card output should
   *   render inside the MUI-like outlined field shell.
   * - If no explicit presentation override is provided, we keep the existing
   *   route-aware default behavior:
   *     • Published/live viewer routes: outlined generated-output field
   *     • Builder/public-preview routes: existing non-outlined presentation
   *
   * This prevents FormPreview from accidentally inheriting the builder-route
   * default when it is intentionally rendering final submitted output.
   */
  const getSubmissionUIComponent = useCallback((options = {}) => {
    const {
      renderDisplayCardOutputAsOutlinedField: renderDisplayCardOutputAsOutlinedFieldOverride,
    } = options || {};

    // ✅ Viewer route: prevent “flash” of wrong action while Firestore is resolving.
    // Builder and public preview keep current behavior.
    if (!isBuilderRoute && !isPublicPreviewMode && isLoading) {
      return null;
    }

    // Published/live viewer routes should render the submitted Display Card output
    // inside a MUI-like outlined field. Builder route and public preview keep the
    // existing DisplayCardPreview presentation unchanged unless a consuming page
    // explicitly requests the outlined output presentation.
    const renderDisplayCardOutputAsOutlinedField =
      typeof renderDisplayCardOutputAsOutlinedFieldOverride === 'boolean'
        ? renderDisplayCardOutputAsOutlinedFieldOverride
        : !isBuilderRoute && !isPublicPreviewMode;

    if (submissionSettings.displayInCard?.enabled) {
      return (
        <DisplayCardPreview
          submittedValues={submissionSettings.displayInCard?.submittedValues || {}}
          mode="final"
          renderOutputAsOutlinedField={renderDisplayCardOutputAsOutlinedField}
        />
      );
    }

    /* ──────────────────────────────────────────────────────────────
       🆕 Display Table branch (no submitted-row logic yet)
          – Shows the table with column settings / stub rows
     ────────────────────────────────────────────────────────────── */
    if (submissionSettings.addToSubmissionsTable?.enabled) {
      return (
        <DisplayTablePreview
          /* No submittedValues prop yet; table will
             render its own stub/example rows */
          mode="final"
        />
      );
    }

    return null;
  }, [
    isBuilderRoute,
    isPublicPreviewMode,
    isLoading,
    submissionSettings,
  ]);


  /**
   * 7️⃣ Provide the context value, including getSubmissionUIComponent
   */
 const contextValue = useMemo(() => ({
   submissionSettings,
   updateSettings,
   updateTableSurfaceSettings,
   patchActionRuntime,       
   getSubmissionUIComponent,
   activeActionType,
   isDisplayInCardEnabled,
   isLoading,
   error,
 }), [
   submissionSettings,
   updateSettings,
   updateTableSurfaceSettings,
   patchActionRuntime,       
   getSubmissionUIComponent,
   activeActionType,
   isDisplayInCardEnabled,
   isLoading,
   error,
 ]);



  return (
    <FormSubmissionSettingsContext.Provider value={contextValue}>
      {children}
    </FormSubmissionSettingsContext.Provider>
  );
};

export default FormSubmissionSettingsProvider;
